import { createContext, useContext } from 'react';
import noop from 'lodash/noop';
import type { Pixels } from '../../../../../../types';

type ScrollBounding = {
	scrollTop: Pixels;
	scrollLeft: Pixels;
	scrollRight: Pixels;
	scrollHeight: Pixels;
	scrollWidth: Pixels;
	isOverflowY: boolean;
};

type RequestBoundingClient = () => ClientRect;
type RequestScrollBounding = () => ScrollBounding;
type ScrollListenerFunction = (callback: EventListener) => void;

type ViewportContextProps = {
	requestViewportBoundingClient: RequestBoundingClient;
	requestViewportScrollBounding: RequestScrollBounding;
	listenToViewportScroll: ScrollListenerFunction;
	stopListeningToViewportScroll: ScrollListenerFunction;
	setRef: (elem: HTMLElement | null) => void;
};

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const DEFAULT_BOUNDING_CLIENT = {
	width: 0,
	height: 0,
	top: 0,
	right: 0,
	bottom: 0,
	left: 0,
} as ClientRect;

const DEFAULT_SCROLL_BOUNDING = {
	scrollTop: 0,
	scrollLeft: 0,
	scrollRight: 0,
	scrollHeight: 0,
	scrollWidth: 0,
	isOverflowY: false,
} as const;

const ViewportContext = createContext<ViewportContextProps>({
	requestViewportBoundingClient: () => DEFAULT_BOUNDING_CLIENT,
	requestViewportScrollBounding: () => DEFAULT_SCROLL_BOUNDING,
	listenToViewportScroll: noop,
	stopListeningToViewportScroll: noop,
	setRef: noop,
});

const { Provider, Consumer } = ViewportContext;

// custom hook, useful for mocking if you cannot mock useContext
export const useViewport = (): ViewportContextProps => {
	const viewportContext = useContext(ViewportContext);
	return viewportContext;
};

export default ViewportContext;
export { Provider, Consumer };
export type {
	ScrollBounding,
	RequestBoundingClient,
	RequestScrollBounding,
	ScrollListenerFunction,
	ViewportContextProps,
};
